import { assertNever, definedMap } from "@glideapps/ts-necessities";

import type { PrimitiveValue } from "../components/computation-types";
import { cleanNumber } from "../components/functions";
import { generateImageFromSeed } from "../components/triangle-image";
import type {
    Formula,
    PrimitiveGlideTypeKind} from "../generator/description";
import {
    BinaryMathFunction,
    FormulaKind,
    isDateOrDateTimeTypeKind,
    UnaryMathFunction,
} from "../generator/description";
import type {
    TextTemplateToken} from "../Support";
import {
    assert,
    convertGlideDurationToJS,
    convertJSDurationToGlide,
    defined,
    isEmptyOrUndefined,
    makeEndOfDayTimeZoneAgnostic,
    makeStartOfDayTimeZoneAgnostic,
    mapFilterUndefined,
    panic,
    tokenizeTemplateString,
} from "../Support";
import { getValueAt, makePathOrGroundValueGetter } from "./handlers";
import type { Condition, ConditionValuePath, PathOrGroundValue } from "./conditions";
import { computeCondition, getPathsForCondition } from "./conditions";
import type {
    GroundValue,
    Table} from "./data";
import {
    asMaybeNumber,
    asMaybeString,
    asPrimitive,
    asString,
    isPrimitive,
    follow,
    asMaybeDate,
    isRow,
    isTable,
    tableForEach,
    isArrayValue,
    arrayForEach,
    isPrimitiveValue,
} from "./data";
import type { Computation, Namespace, RootPathResolver } from "./types";
import type { Path, RelativePath, RootPath } from "./paths";
import { formatNumberFixed } from "../format-number";
import type { FormatNumberSpecification } from "../generator/formulas/formula-specifications";
import { getWeekNumber, log2, modulo, round2, trunc2 } from "../math-functions";
import { DateTimeAsyncParser } from "./parse-date-time";

export class StaticTextTemplateComputation implements Computation {
    constructor(private readonly _tokens: readonly TextTemplateToken<Path>[]) {}

    public getPaths(): readonly Path[] {
        return mapFilterUndefined(this._tokens, t => (!t.isText ? t.value : undefined));
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const parts: string[] = [];
        for (const t of this._tokens) {
            if (t.isText) {
                parts.push(t.text);
            } else {
                const v = getValueAt(ns, context, t.value);
                if (!isPrimitive(v)) continue;
                const s = asMaybeString(v);
                if (s === undefined) continue;
                parts.push(s);
            }
        }
        return parts.join("");
    }
}

export class DynamicTextTemplateComputation implements Computation {
    constructor(private readonly _templatePath: Path, private readonly _replacementPaths: ReadonlyMap<string, Path>) {}

    public getPaths(): readonly Path[] {
        return [this._templatePath, ...this._replacementPaths.values()];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const template = asMaybeString(asPrimitive(getValueAt(ns, context, this._templatePath)));
        if (template === undefined) return undefined;

        const tokens = tokenizeTemplateString(template, Array.from(this._replacementPaths.keys()));
        const parts = tokens.map(t => {
            if (t.isText) {
                return t.text;
            } else {
                const path = defined(this._replacementPaths.get(t.value));
                return asString(getValueAt(ns, context, path));
            }
        });
        return parts.join("");
    }
}

export class FormatNumberComputation implements Computation {
    constructor(private readonly _numberPath: Path, private readonly _spec: FormatNumberSpecification) {}

    public getPaths(): readonly Path[] {
        return [this._numberPath];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const n = asMaybeNumber(getValueAt(ns, context, this._numberPath));
        if (n === undefined || isNaN(n)) return undefined;

        const spec = this._spec;
        // ##formatCurrency:
        // FIXME: Unify this code.  Until then, keep it in sync.
        if (spec.unit === "$" && !spec.unitAfterNumber) {
            return formatNumberFixed(n, spec.decimalsAfterPoint, spec.groupSeparator, "USD");
        } else {
            const s = formatNumberFixed(n, spec.decimalsAfterPoint, spec.groupSeparator, undefined);
            if (spec.unit === undefined) {
                return s;
            }
            if (spec.unitAfterNumber) {
                return s + spec.unit;
            } else {
                return spec.unit + s;
            }
        }
    }
}

export class GenerateImageComputation implements Computation {
    constructor(private readonly _seedPath: Path, private readonly _isMesh: boolean) {}

    public getPaths(): readonly Path[] {
        return [this._seedPath];
    }

    public compute(resolver: RootPathResolver, context: GroundValue): GroundValue {
        const seed = asString(getValueAt(resolver, context, this._seedPath));
        if (seed === "") return undefined;
        return generateImageFromSeed(seed, this._isMesh);
    }
}

class UndefinedDate {}
const undefinedDate = new UndefinedDate();

export class MathComputation extends DateTimeAsyncParser implements Computation {
    constructor(
        private readonly _formula: Formula,
        private readonly _variablePaths: ReadonlyMap<string, [Path, PrimitiveGlideTypeKind]>
    ) {
        super();
    }

    public getPaths(): readonly Path[] {
        return Array.from(this._variablePaths.values()).map(([p]) => p);
    }

    public compute(ns: RootPathResolver, context: GroundValue, setAllDirty: () => void): GroundValue {
        const compute = (f: Formula): PrimitiveValue | UndefinedDate => {
            switch (f.kind) {
                case FormulaKind.Constant:
                    return f.value;
                case FormulaKind.Random:
                    return cleanNumber(Math.random());
                case FormulaKind.UnaryMath: {
                    const operandValue = compute(f.operand);

                    // Handle date/time operations first.
                    if (operandValue instanceof UndefinedDate) return undefined;
                    switch (f.fn) {
                        case UnaryMathFunction.Year: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            return date?.getUTCFullYear();
                        }
                        case UnaryMathFunction.Month: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            if (date === undefined) return undefined;
                            return date.getUTCMonth() + 1;
                        }
                        case UnaryMathFunction.Day: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            return date?.getUTCDate();
                        }
                        case UnaryMathFunction.Hour: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            return date?.getUTCHours();
                        }
                        case UnaryMathFunction.Minute: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            return date?.getUTCMinutes();
                        }
                        case UnaryMathFunction.Second: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            return date?.getUTCSeconds();
                        }
                        case UnaryMathFunction.Weekday: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            if (date === undefined) return undefined;
                            return date.getUTCDay() + 1;
                        }
                        case UnaryMathFunction.WeekNumber: {
                            const date = this.parseDateTime(operandValue, setAllDirty);
                            if (date === undefined) return undefined;
                            return getWeekNumber(date);
                        }
                        default:
                            break;
                    }

                    const operand = asMaybeNumber(operandValue);
                    if (operand === undefined) return undefined;
                    let result: number;
                    switch (f.fn) {
                        case UnaryMathFunction.Negate:
                            result = -operand;
                            break;
                        case UnaryMathFunction.Absolute:
                            result = Math.abs(operand);
                            break;
                        case UnaryMathFunction.Floor:
                            result = Math.floor(operand);
                            break;
                        case UnaryMathFunction.Ceiling:
                            result = Math.ceil(operand);
                            break;
                        case UnaryMathFunction.Round:
                            result = Math.round(operand);
                            break;
                        case UnaryMathFunction.Sign:
                            result = Math.sign(operand);
                            break;
                        case UnaryMathFunction.Truncate:
                            result = Math.trunc(operand);
                            break;
                        case UnaryMathFunction.Sine:
                            result = Math.sin(operand);
                            break;
                        case UnaryMathFunction.Cosine:
                            result = Math.cos(operand);
                            break;
                        case UnaryMathFunction.Tangent:
                            result = Math.tan(operand);
                            break;
                        case UnaryMathFunction.ArcSine:
                            result = Math.asin(operand);
                            break;
                        case UnaryMathFunction.ArcCosine:
                            result = Math.acos(operand);
                            break;
                        case UnaryMathFunction.ArcTangent:
                            result = Math.atan(operand);
                            break;
                        case UnaryMathFunction.Logarithm:
                            result = Math.log10(operand);
                            break;
                        default:
                            return undefined;
                    }
                    return cleanNumber(result);
                }
                case FormulaKind.BinaryMath: {
                    const aValue = compute(f.left);
                    const bValue = compute(f.right);
                    if (bValue instanceof UndefinedDate) return undefined;
                    const b = asMaybeNumber(bValue);

                    // ##dateMathInNewComputationModel:
                    // FIXME: The formulas don't contain type information, we
                    // only bring it in via the type kind in `GetVariable`
                    // below. The NCM doesn't compile formulas yet, so we're
                    // dispatching on the type dynamically.  This is slow.
                    if (aValue instanceof Date) {
                        switch (f.fn) {
                            case BinaryMathFunction.Add:
                                if (b === undefined) return undefined;
                                return new Date(aValue.getTime() + convertGlideDurationToJS(b));
                            case BinaryMathFunction.Subtract:
                                if (bValue === undefined) return undefined;
                                if (bValue instanceof Date) {
                                    return cleanNumber(convertJSDurationToGlide(aValue.getTime() - bValue.getTime()));
                                } else if (b !== undefined) {
                                    return new Date(aValue.getTime() - convertGlideDurationToJS(b));
                                }
                                return undefined;
                            default:
                                // logError("Invalid math on date", f.fn);
                                return undefined;
                        }
                    }
                    if (aValue instanceof UndefinedDate) {
                        switch (f.fn) {
                            case BinaryMathFunction.Add:
                                return undefinedDate;
                            case BinaryMathFunction.Subtract:
                                return undefined;
                            default:
                                // logError("Invalid math on date", f.fn);
                                return undefined;
                        }
                    }

                    const a = asMaybeNumber(aValue);

                    switch (f.fn) {
                        case BinaryMathFunction.Add:
                            if (a === undefined && b === undefined) return undefined;
                            if (a === undefined) {
                                if (f.right.kind === FormulaKind.Constant) return undefined;
                                return b;
                            }
                            if (b === undefined) {
                                if (f.left.kind === FormulaKind.Constant) return undefined;
                                return a;
                            }
                            return cleanNumber(a + b);
                        case BinaryMathFunction.Subtract:
                            if (a === undefined) return undefined;
                            if (b === undefined) {
                                if (f.left.kind === FormulaKind.Constant) return undefined;
                                return a;
                            }
                            return cleanNumber(a - b);
                    }

                    if (a === undefined || b === undefined) return undefined;

                    let result: number | undefined;
                    switch (f.fn) {
                        case BinaryMathFunction.Multiply:
                            result = a * b;
                            break;
                        case BinaryMathFunction.Divide:
                            result = a / b;
                            break;
                        case BinaryMathFunction.Modulo:
                            result = modulo(a, b);
                            break;
                        case BinaryMathFunction.Minimum:
                            result = Math.min(a, b);
                            break;
                        case BinaryMathFunction.Maximum:
                            result = Math.max(a, b);
                            break;
                        case BinaryMathFunction.Power:
                            result = Math.pow(a, b);
                            break;
                        case BinaryMathFunction.Logarithm:
                            result = log2(a, b);
                            break;
                        case BinaryMathFunction.ArcTangent2:
                            result = Math.atan2(a, b);
                            break;
                        case BinaryMathFunction.Round:
                            result = round2(a, b);
                            break;
                        case BinaryMathFunction.Truncate:
                            result = trunc2(a, b);
                            break;
                        default:
                            return panic("FIXME: implement");
                    }
                    return definedMap(result, cleanNumber);
                }
                case FormulaKind.GetVariable:
                    const p = this._variablePaths.get(f.name);
                    if (p === undefined) return undefined;
                    const [path, kind] = p;
                    const v = getValueAt(ns, context, path);
                    if (isDateOrDateTimeTypeKind(kind)) {
                        if (!isPrimitive(v)) return undefinedDate;
                        return this.parseDateTime(v, setAllDirty) ?? undefinedDate;
                    } else {
                        return v;
                    }
                default:
                    return panic("FIXME: implement");
            }
        };
        // The old computation model just passes through values if the formula
        // is just something like `x`.  It doesn't coerce into number or date,
        // so we don't, either.
        if (this._formula.kind === FormulaKind.GetVariable) {
            const p = this._variablePaths.get(this._formula.name);
            if (p === undefined) return undefined;
            const [path] = p;
            return getValueAt(ns, context, path);
        }

        const result = compute(this._formula);
        if (result instanceof UndefinedDate) return undefined;
        return result;
    }
}

export class ConditionComputation extends DateTimeAsyncParser implements Computation {
    constructor(private readonly _condition: Condition<ConditionValuePath>) {
        super();
    }

    public getPaths(): readonly Path[] {
        assert(getPathsForCondition(this._condition, true).length === 0);
        return getPathsForCondition(this._condition, false);
    }

    public compute(ns: RootPathResolver, context: GroundValue, setAllDirty: () => void): GroundValue {
        const get = makePathOrGroundValueGetter(ns, context, undefined);
        return computeCondition(this._condition, get, v => this.parseDateTime(v, setAllDirty));
    }
}

export interface IfThenElseClause {
    readonly condition: Condition<ConditionValuePath>;
    readonly then: PathOrGroundValue<ConditionValuePath>;
}

export class IfThenElseComputation extends DateTimeAsyncParser implements Computation {
    constructor(
        private readonly _clauses: readonly IfThenElseClause[],
        private readonly _else: PathOrGroundValue<ConditionValuePath>
    ) {
        super();
    }

    public getPaths(): readonly Path[] {
        const paths: Path[] = [];

        for (const c of this._clauses) {
            assert(getPathsForCondition(c.condition, true).length === 0);
            paths.push(...getPathsForCondition(c.condition, false));
            if (c.then.isPath) paths.push(c.then.path.path);
        }
        if (this._else.isPath) paths.push(this._else.path.path);

        return paths;
    }

    public compute(ns: RootPathResolver, context: GroundValue, setAllDirty: () => void): GroundValue {
        const get = makePathOrGroundValueGetter(ns, context, undefined);

        for (const c of this._clauses) {
            const result = computeCondition(c.condition, get, v => this.parseDateTime(v, setAllDirty));
            // `undefined` defaults to `false`
            if (result === true) {
                return c.then.isPath ? get(c.then.path) : c.then.value;
            }
        }
        return this._else.isPath ? get(this._else.path) : this._else.value;
    }
}

export class SplitStringComputation implements Computation {
    constructor(private readonly _inputPath: Path, private readonly _separatorPath: Path) {}

    public getPaths(): readonly Path[] {
        return [this._inputPath, this._separatorPath];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const input = asMaybeString(getValueAt(ns, context, this._inputPath));
        const separator = asMaybeString(getValueAt(ns, context, this._separatorPath));
        if (input === undefined || separator === undefined) return undefined;
        return input
            .split(separator)
            .map(s => s.trim())
            .filter(s => s !== "");
    }
}

export class MakeArrayComputation implements Computation {
    constructor(private readonly _paths: readonly Path[]) {}

    public getPaths(): readonly Path[] {
        return this._paths;
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        return mapFilterUndefined(this._paths, p => {
            const v = getValueAt(ns, context, p);
            if (v === undefined) return undefined;
            assert(isPrimitive(v));
            return v;
        });
    }
}

export class ConstructURLComputation implements Computation {
    constructor(
        private readonly _schemePath: Path,
        private readonly _hostPath: Path,
        private readonly _pathPath: Path,
        private readonly _queryParameterPaths: ReadonlyMap<string, Path>
    ) {}

    public getPaths(): readonly Path[] {
        return [this._schemePath, this._hostPath, this._pathPath, ...Array.from(this._queryParameterPaths.values())];
    }

    public compute(ns: RootPathResolver, context: GroundValue): GroundValue {
        const host = definedMap(getValueAt(ns, context, this._hostPath), asString);
        if (isEmptyOrUndefined(host)) return undefined;

        const schemeValue = getValueAt(ns, context, this._schemePath);

        let scheme = definedMap(schemeValue, asString)?.trim();
        if (scheme === undefined || scheme === "") {
            scheme = "https";
        }

        let path = definedMap(getValueAt(ns, context, this._pathPath), asString)?.trim();
        if (isEmptyOrUndefined(path)) {
            path = "/";
        } else if (!path.startsWith("/")) {
            path = "/" + path;
        }

        const query = mapFilterUndefined(this._queryParameterPaths.entries(), ([k, p]) => {
            const v = definedMap(getValueAt(ns, context, p), asString);
            if (v === undefined) return undefined;
            return `${encodeURIComponent(k)}=${encodeURIComponent(asString(v))}`;
        }).join("&");

        let url = `${scheme}://${host}${path}`;
        if (query.length > 0) {
            url = url + "?" + query;
        }

        return url;
    }
}

// ##singleLookupViaCombine:
// FIXME: Do this via combine instead, clearing the whole table.
export class SingleLookupComputation implements Computation {
    // `_columnInRelationTablePath` is only here to dirty us if the looked-up
    // column changes.
    constructor(
        private readonly _relationPath: Path,
        private readonly _columnInRelationPath: RelativePath,
        private readonly _columnInRelationTablePath: RootPath | undefined
    ) {}

    public getPaths(): readonly Path[] {
        if (this._columnInRelationTablePath === undefined) {
            return [this._relationPath];
        } else {
            return [this._relationPath, this._columnInRelationTablePath];
        }
    }

    public compute(ns: Namespace, row: GroundValue): GroundValue {
        return follow(getValueAt(ns, row, this._relationPath), this._columnInRelationPath);
    }
}

export enum ModifyDateKind {
    StartOfDay = "start-of-day",
    EndOfDay = "end-of-day",
}

export class ModifyDateComputation implements Computation {
    constructor(private readonly _datePath: Path, private readonly _kind: ModifyDateKind) {}

    public getPaths(): readonly Path[] {
        return [this._datePath];
    }

    public compute(ns: Namespace, row: GroundValue): GroundValue {
        const date = asMaybeDate(getValueAt(ns, row, this._datePath));
        if (date === undefined) return undefined;

        switch (this._kind) {
            case ModifyDateKind.StartOfDay:
                return makeStartOfDayTimeZoneAgnostic(date);
            case ModifyDateKind.EndOfDay:
                return makeEndOfDayTimeZoneAgnostic(date);
            default:
                return assertNever(this._kind);
        }
    }
}

export enum ForwardingKind {
    LookupPrimitive,
    LookupRow,
    SingleValue,
}

export class GlobalGuardedForwardComputation implements Computation {
    constructor(
        private readonly _forwardeePath: RootPath,
        private readonly _arbiterPath: Path,
        private readonly _kind: ForwardingKind
    ) {}

    public getPaths(): readonly Path[] {
        return [this._forwardeePath, this._arbiterPath];
    }

    public compute(ns: Namespace, row: GroundValue): GroundValue {
        const arbiter = getValueAt(ns, row, this._arbiterPath);
        const forwardee = ns.get(this._forwardeePath);

        if (this._kind === ForwardingKind.SingleValue) {
            if (arbiter === undefined) return undefined;
            if (isTable(arbiter) && arbiter.size === 0) return undefined;
            return forwardee;
        }

        if (isRow(arbiter)) {
            return forwardee;
        } else if (isTable(arbiter)) {
            const tableResult: Table = new Map();
            const primitiveResults: PrimitiveValue[] = [];
            tableForEach(arbiter, false, () => {
                if (forwardee === undefined) return;
                if (isRow(forwardee)) {
                    tableResult.set(forwardee.$rowID, forwardee);
                } else if (isArrayValue(forwardee)) {
                    arrayForEach(forwardee, v => {
                        if (v === undefined) return;
                        if (isPrimitiveValue(v)) {
                            primitiveResults.push(v);
                        }
                    });
                } else if (isPrimitiveValue(forwardee)) {
                    primitiveResults.push(forwardee);
                }
            });
            if (this._kind === ForwardingKind.LookupRow) {
                return tableResult;
            } else {
                return primitiveResults;
            }
        }

        return undefined;
    }
}
